/**
 * equityTypes.ts —— 赢率 Worker 消息协议与快照类型
 *
 * 放在独立文件里，保证 Worker 侧与主线程侧共享同一份类型，且 Worker 不会因为
 * 顺带 import UI 模块而把 DOM 依赖拖进子线程。
 */

import type { Card } from '../core/cards';
import type { CancelReason, EquityResult, Street } from '../core/equity';

/** 主线程 → Worker。 */
export type EquityRequest =
  | {
      readonly type: 'calc';
      readonly requestId: number;
      readonly hole: readonly Card[];
      readonly board: readonly Card[];
      /**
       * 调试用（诊断包专用）：本次计算额外注入的**总耗时预算**（ms）。
       * Worker 会把它切成 120ms 的薄片摊到真实进度检查点之后，
       * 因此只拉长总耗时，不伪造进度、不改变任何结果数值。
       */
      readonly slowBurnMs?: number;
    }
  | { readonly type: 'cancel'; readonly requestId: number; readonly reason: CancelReason };

/** Worker → 主线程。 */
export type EquityResponse =
  | {
      readonly type: 'progress';
      readonly requestId: number;
      readonly street: Street;
      readonly boardsEnumerated: number;
      readonly boardsTotal: number;
      readonly ratio: number;
      readonly elapsedMs: number;
    }
  | { readonly type: 'result'; readonly requestId: number; readonly result: EquityResult }
  | {
      readonly type: 'error';
      readonly requestId: number;
      readonly kind: 'unavailable' | 'cancelled' | 'failed';
      readonly message: string;
      readonly detail?: string;
    };

/** 某个节点的赢率快照（写入手牌历史，供复盘读取、不重算 —— AC4.9）。 */
export interface EquitySnapshot {
  readonly street: Street;
  readonly win: number;
  readonly tie: number;
  readonly lose: number;
  readonly equity: number;
  readonly fromTable: boolean;
  readonly elapsedMs: number;
  readonly boardsTotal: number;
}

/** handNumber → street → 快照。 */
export type EquitySnapshotMap = Readonly<Record<number, Readonly<Partial<Record<Street, EquitySnapshot>>>>>;

export function toSnapshot(r: EquityResult): EquitySnapshot {
  return {
    street: r.street,
    win: r.win,
    tie: r.tie,
    lose: r.lose,
    equity: r.equity,
    fromTable: r.fromTable,
    elapsedMs: r.elapsedMs,
    boardsTotal: r.boardsTotal,
  };
}

/** PRD 4.6 各街耗时上限（中端机基准）。超出只上报 calc_slow，不改口径、不降精度。 */
export const STREET_TIME_BUDGET_MS: Readonly<Record<Street, number>> = {
  preflop: 5,
  flop: 50,
  turn: 10,
  river: 5,
};

/** ≤300ms 只出 skeleton；>300ms 才出真实进度（设计方案原则 4）。 */
export const LOADING_THRESHOLD_MS = 300;
